/* ==========================================================================
   ATLAS — front-end behaviour for the Atlas sections.
   Everything is scoped to a section root, so multiple instances and
   theme-editor section reloads behave correctly.
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var instances = {};

  /* ---------------- Money formatting ---------------- */
  function formatMoney(cents, format) {
    var fmt = format || '${{amount}}';
    var match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    var key = match ? match[1] : 'amount';
    var precision = key.indexOf('no_decimals') !== -1 ? 0 : 2;
    var thousands = ',';
    var decimal = '.';

    if (key.indexOf('comma_separator') !== -1) {
      thousands = '.';
      decimal = ',';
    } else if (key.indexOf('apostrophe_separator') !== -1) {
      thousands = "'";
    }

    var parts = (Math.abs(cents || 0) / 100).toFixed(precision).split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    var amount = parts[1] ? whole + decimal + parts[1] : whole;

    return fmt.replace(/\{\{\s*\w+\s*\}\}/, amount);
  }

  /* ---------------- Gallery ---------------- */
  function initGallery(root, teardown) {
    var stage = root.querySelector('[data-atlas-stage]');
    if (!stage) return;

    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-atlas-slide]'));
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-atlas-thumb]'));
    var bar = root.querySelector('[data-atlas-progress]');
    var current = root.querySelector('[data-atlas-current]');
    var lightbox = root.querySelector('[data-atlas-lightbox]');
    var lightboxImg = lightbox && lightbox.querySelector('[data-atlas-lightbox-image]');
    var closeBtn = lightbox && lightbox.querySelector('[data-atlas-lightbox-close]');
    var duration = parseInt(stage.getAttribute('data-autoplay'), 10) || 0;
    var index = 0;
    var raf = null;
    var startedAt = null;

    if (slides.length === 0) return;

    function go(next) {
      index = (next + slides.length) % slides.length;
      slides.forEach(function (slide, i) {
        slide.classList.toggle('is-active', i === index);
      });
      thumbs.forEach(function (thumb, i) {
        thumb.classList.toggle('is-active', i === index);
        thumb.setAttribute('aria-selected', String(i === index));
      });
      if (current) current.textContent = String(index + 1);
      restart();
    }

    function tick(time) {
      if (startedAt === null) startedAt = time;
      var progress = Math.min((time - startedAt) / duration, 1);
      if (bar) bar.style.width = progress * 100 + '%';
      if (progress >= 1) {
        go(index + 1);
        return;
      }
      raf = window.requestAnimationFrame(tick);
    }

    function pause() {
      if (raf) window.cancelAnimationFrame(raf);
      raf = null;
    }

    function restart() {
      pause();
      startedAt = null;
      if (bar) bar.style.width = '0';
      if (!reduceMotion && duration > 0 && slides.length > 1) {
        raf = window.requestAnimationFrame(tick);
      }
    }

    var prev = root.querySelector('[data-atlas-prev]');
    var next = root.querySelector('[data-atlas-next]');
    if (prev) prev.addEventListener('click', function () { go(index - 1); });
    if (next) next.addEventListener('click', function () { go(index + 1); });
    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () { go(i); });
    });

    stage.addEventListener('mouseenter', pause);
    stage.addEventListener('mouseleave', restart);

    function onKeydown(event) {
      if (lightbox && lightbox.classList.contains('is-open')) {
        if (event.key === 'Escape') closeLightbox();
        return;
      }
      if (!root.contains(document.activeElement)) return;
      if (event.target.closest && event.target.closest('input, textarea, select')) return;
      if (event.key === 'ArrowLeft') go(index - 1);
      if (event.key === 'ArrowRight') go(index + 1);
    }
    document.addEventListener('keydown', onKeydown);
    teardown.push(function () {
      document.removeEventListener('keydown', onKeydown);
      pause();
    });

    var touchStart = null;
    stage.addEventListener('touchstart', function (event) {
      touchStart = event.touches[0].clientX;
    }, { passive: true });
    stage.addEventListener('touchend', function (event) {
      if (touchStart === null) return;
      var delta = event.changedTouches[0].clientX - touchStart;
      if (Math.abs(delta) > 40) go(index + (delta < 0 ? 1 : -1));
      touchStart = null;
    });

    if (window.matchMedia('(hover: hover)').matches && stage.hasAttribute('data-zoom')) {
      stage.addEventListener('mousemove', function (event) {
        if (event.target.closest('[data-atlas-stage-nav]')) {
          stage.classList.remove('is-zooming');
          return;
        }
        var rect = stage.getBoundingClientRect();
        var image = slides[index].querySelector('img');
        if (!image) return;
        image.style.transformOrigin =
          ((event.clientX - rect.left) / rect.width) * 100 + '% ' +
          ((event.clientY - rect.top) / rect.height) * 100 + '%';
        stage.classList.add('is-zooming');
      });
      stage.addEventListener('mouseleave', function () {
        stage.classList.remove('is-zooming');
      });
    }

    function closeLightbox() {
      if (!lightbox) return;
      lightbox.classList.remove('is-open');
      document.body.style.overflow = '';
      restart();
    }

    if (lightbox && lightboxImg) {
      slides.forEach(function (slide) {
        slide.addEventListener('click', function () {
          var image = slide.querySelector('img');
          if (!image) return;
          lightboxImg.src = slide.getAttribute('data-full') || image.currentSrc || image.src;
          lightboxImg.alt = image.alt || '';
          lightbox.classList.add('is-open');
          document.body.style.overflow = 'hidden';
          pause();
          if (closeBtn) closeBtn.focus();
        });
      });
      if (closeBtn) closeBtn.addEventListener('click', closeLightbox);
      lightbox.addEventListener('click', function (event) {
        if (event.target === lightbox) closeLightbox();
      });
      teardown.push(closeLightbox);
    }

    restart();
  }

  /* ---------------- Sticky gallery ----------------
     CSS `bottom` stickiness only pins an element as it enters from below, so
     it cannot hold a column taller than the viewport while scrolling down.
     Measuring the column and setting a negative `top` sticks it by its bottom
     edge instead, keeping the thumbs and progress bar in view. */
  function initStickyGallery(root, teardown) {
    var gallery = root.querySelector('[data-atlas-gallery]');
    if (!gallery) return;

    var GAP = 32;
    var observer = null;

    function headerHeight() {
      var raw = window.getComputedStyle(document.documentElement).getPropertyValue('--atlas-header-h');
      var value = parseFloat(raw);
      return isNaN(value) ? 88 : value;
    }

    function update() {
      // The stacked mobile layout drops out of sticky entirely.
      if (window.getComputedStyle(gallery).position !== 'sticky') {
        gallery.style.top = '';
        return;
      }
      var top = Math.min(headerHeight() + GAP, window.innerHeight - gallery.offsetHeight - GAP);
      gallery.style.top = Math.round(top) + 'px';
    }

    update();
    window.addEventListener('resize', update);

    if ('ResizeObserver' in window) {
      observer = new ResizeObserver(update);
      observer.observe(gallery);
    }

    teardown.push(function () {
      window.removeEventListener('resize', update);
      if (observer) observer.disconnect();
      gallery.style.top = '';
    });
  }

  /* ---------------- Price, variants, quantity ---------------- */
  function initPurchase(root) {
    var buy = root.querySelector('[data-atlas-buy]');
    if (!buy) return;

    var priceEl = root.querySelector('[data-atlas-price]');
    var barPriceEl = root.querySelector('[data-atlas-bar-price]');
    var qtyInput = root.querySelector('[data-atlas-qty]');
    var variantInput = root.querySelector('[data-atlas-variant-input]');
    var radios = Array.prototype.slice.call(root.querySelectorAll('[data-atlas-variant]'));
    var submits = Array.prototype.slice.call(root.querySelectorAll('[data-atlas-submit]'));
    var stockEl = root.querySelector('[data-atlas-stock]');
    var moneyFormat = buy.getAttribute('data-money-format') || '${{amount}}';
    var multiply = buy.getAttribute('data-multiply') === 'true';
    var maxQty = parseInt(buy.getAttribute('data-max-quantity'), 10) || 10;

    function selected() {
      var checked = radios.filter(function (radio) { return radio.checked; })[0];
      return checked || radios[0] || null;
    }

    function quantity() {
      if (!qtyInput) return 1;
      var value = Math.max(1, Math.min(maxQty, parseInt(qtyInput.value, 10) || 1));
      qtyInput.value = value;
      return value;
    }

    function render() {
      var qty = quantity();
      var radio = selected();

      var unit = radio ? parseInt(radio.getAttribute('data-price'), 10) : parseInt(buy.getAttribute('data-price'), 10);
      var compare = radio
        ? parseInt(radio.getAttribute('data-compare-price'), 10)
        : parseInt(buy.getAttribute('data-compare-price'), 10);
      var available = radio ? radio.getAttribute('data-available') === 'true' : buy.getAttribute('data-available') === 'true';

      if (radio && variantInput) variantInput.value = radio.value;

      if (!isNaN(unit) && priceEl) {
        var total = multiply ? unit * qty : unit;
        var money = formatMoney(total, moneyFormat);
        priceEl.innerHTML = money;
        if (!isNaN(compare) && compare > unit) {
          priceEl.innerHTML += ' <s>' + formatMoney(multiply ? compare * qty : compare, moneyFormat) + '</s>';
        }
        if (barPriceEl) barPriceEl.innerHTML = money;
      }

      if (stockEl) {
        var inStockText = stockEl.getAttribute('data-in-stock');
        var outStockText = stockEl.getAttribute('data-out-of-stock');
        if (inStockText !== null && outStockText !== null) {
          stockEl.textContent = available ? inStockText : outStockText;
        }
        stockEl.classList.toggle('atlas-stock--out', !available);
      }

      submits.forEach(function (button) {
        button.disabled = !available;
        var label = available
          ? button.getAttribute('data-label-available')
          : button.getAttribute('data-label-sold-out');
        if (label) button.textContent = label;
      });
    }

    Array.prototype.slice.call(root.querySelectorAll('[data-atlas-qty-step]')).forEach(function (button) {
      button.addEventListener('click', function () {
        if (!qtyInput) return;
        qtyInput.value = (parseInt(qtyInput.value, 10) || 1) + parseInt(button.getAttribute('data-atlas-qty-step'), 10);
        render();
      });
    });
    if (qtyInput) qtyInput.addEventListener('change', render);
    radios.forEach(function (radio) { radio.addEventListener('change', render); });

    render();
  }

  /* ---------------- Countdown ---------------- */
  function initCountdown(root, teardown) {
    var clock = root.querySelector('[data-atlas-clock]');
    if (!clock) return;

    var target = new Date(clock.getAttribute('data-launch')).getTime();
    if (isNaN(target)) return;

    var units = { d: 864e5, h: 36e5, m: 6e4, s: 1e3 };
    var wrapper = clock.closest('[data-atlas-launch]') || clock;
    var openMarkup = clock.getAttribute('data-open-text');
    var timer = null;

    function update() {
      var remaining = target - Date.now();
      if (remaining <= 0) {
        if (openMarkup) {
          wrapper.innerHTML = '<p><strong>' + openMarkup + '</strong></p>';
        } else {
          clock.hidden = true;
        }
        return;
      }
      ['d', 'h', 'm', 's'].forEach(function (unit) {
        var value = Math.floor(remaining / units[unit]);
        remaining -= value * units[unit];
        var node = clock.querySelector('[data-unit="' + unit + '"]');
        if (node) node.textContent = String(value).padStart(2, '0');
      });
      timer = window.setTimeout(update, 1000);
    }

    update();
    teardown.push(function () {
      if (timer) window.clearTimeout(timer);
    });
  }

  /* ---------------- Course accordion ---------------- */
  function initCourses(root) {
    var courses = Array.prototype.slice.call(root.querySelectorAll('[data-atlas-course]'));
    courses.forEach(function (course) {
      course.addEventListener('click', function () {
        var isOpen = course.getAttribute('aria-expanded') === 'true';
        courses.forEach(function (other) { other.setAttribute('aria-expanded', 'false'); });
        course.setAttribute('aria-expanded', String(!isOpen));
      });
    });
  }

  /* ---------------- Mobile buy bar ---------------- */
  function initBuyBar(root, teardown) {
    var bar = root.querySelector('[data-atlas-buybar]');
    var anchor = root.querySelector('[data-atlas-buybar-anchor]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(function (entries) {
      var entry = entries[0];
      var show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', String(!show));
      Array.prototype.slice.call(bar.querySelectorAll('a, button')).forEach(function (node) {
        node.tabIndex = show ? 0 : -1;
      });
    });
    observer.observe(anchor);
    teardown.push(function () { observer.disconnect(); });
  }

  /* ---------------- Boot ---------------- */
  function destroy(id) {
    var fns = instances[id];
    if (!fns) return;
    fns.forEach(function (fn) {
      try { fn(); } catch (error) { /* noop */ }
    });
    delete instances[id];
  }

  function init(root) {
    if (!root) return;
    var id = root.getAttribute('data-atlas-section') || root.id;
    destroy(id);

    var teardown = [];
    instances[id] = teardown;

    initGallery(root, teardown);
    initStickyGallery(root, teardown);
    initPurchase(root);
    initCountdown(root, teardown);
    initCourses(root);
    initBuyBar(root, teardown);
  }

  function initAll(scope) {
    var container = scope || document;
    Array.prototype.slice
      .call(container.querySelectorAll('[data-atlas-section]'))
      .forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    Array.prototype.slice
      .call(event.target.querySelectorAll('[data-atlas-section]'))
      .forEach(function (root) {
        destroy(root.getAttribute('data-atlas-section') || root.id);
      });
  });
})();
